import type { PathsWithMethod } from 'openapi-typescript-helpers';
import type { CompareFn, TypesForGetRequest } from 'swr-openapi';

/**
 * Builds typed SWR key predicates for a service's cache entries.
 *
 * swr-openapi only ships `createMutateHook`, so callers that can't be inside a
 * component — module-scope event handlers, chat tool definitions — otherwise
 * hand-match its key tuples and lose the path and params types. This gives them
 * the same type safety while composing with any `mutate`, including the global
 * one from `swr`.
 *
 * Note the global cache and the one behind `useSWRConfig` are the same cache
 * only while no `<SWRConfig provider>` exists, which this app doesn't set.
 */
export function createKeyMatcher<
  // Matches swr-openapi's own constraint; anything narrower rejects generated
  // `paths` interfaces, which have no string index signature.
  Paths extends {},
>(prefix: string, compare: CompareFn) {
  return function matchesKey<Path extends PathsWithMethod<Paths, 'get'>>(
    path: Path,
    init?: TypesForGetRequest<Paths, Path>['Init'],
  ) {
    return (key: unknown) => {
      // swr-openapi cache keys are tuples: [prefix, path, init]
      if (!Array.isArray(key) || ![2, 3].includes(key.length)) return false;

      const [keyPrefix, keyPath, keyInit] = key;

      return (
        keyPrefix === prefix &&
        keyPath === path &&
        (init ? compare(keyInit, init) : true)
      );
    };
  };
}
